import { decodeHtmlEntities, escapeHtml } from '@/helpers/html'

/**
 * A stored comment (HTML) as text for InputMentionable's textarea, and back.
 *
 * keyvue-api stores a comment with markup with "&" and "<" in its text escaped (CommentHtmlSanitizer). The textarea
 * shows the text decoded, a mention of a known user as "@Name" and a line break as a new line. Saving escapes "&" and
 * "<" in the text again, so text stays text, and builds the markup for mentions and new lines.
 *
 * Markers: invisible characters (Unicode tag characters, which browsers don't draw) that travel with the text while
 * it is edited.
 * - A mention is "@", a marker with the user's id, and the name, so that it stays the mention of that user, also when
 *   two users have the same name. InputMentionable's suggestions insert the same (mentionText()).
 * - Markup of the stored comment that the textarea can't show as text (a mention of a user who isn't in the list, a
 *   tag of one-app's editor such as <p>) is shown as it is stored, after a marker with its number. On saving, it is
 *   markup only there, behind its own marker and unchanged (each piece once); the same characters typed elsewhere are
 *   text.
 * An unchanged text gives back the stored comment exactly.
 */
export interface MentionableUser {
    id: string
    name: string
}

export interface CommentText {
    /** What the textarea shows */
    text: string
    /** The stored comment the text was made from */
    stored: string
    /** The stored markup that the text shows as it is, by marker number */
    keptMarkup: string[]
    /** The names shown for the mentions of the stored comment, by user id */
    mentionNames: { [userId: string]: string }
}

const MENTION_START = '<span class="mention"'

const LINE_BREAK = /^<br\s*\/?>$/i

const TAG = /^<\/?[a-zA-Z][^<>]*>/
/** TAG, matched where lastIndex is (sticky): for splitHtml, without a new string at every "<" */
const TAG_AT = new RegExp(TAG.source.slice(1), 'y')

/** U+E0001 LANGUAGE TAG starts a marker, U+E007F CANCEL TAG ends it; in between, tag characters spell its content */
const MARKER_START = 0xE0001
const MARKER_END = 0xE007F
const TAG_FIRST = 0xE0020
const TAG_LAST = 0xE007E

/** U+1F3F4 WAVING BLACK FLAG: tag characters after it spell a flag (an emoji), they aren't markers */
const FLAG = 0x1F3F4

function marker(kind: 'k' | 'm', content: string): string {
    return String.fromCodePoint(MARKER_START)
        + Array.from(kind + content).map(character => String.fromCodePoint(TAG_FIRST - 0x20 + character.charCodeAt(0))).join('')
        + String.fromCodePoint(MARKER_END)
}

/**
 * The marker that starts at position, if one does: its kind, content and where it ends
 */
function readMarker(text: string, position: number): { kind: string, content: string, end: number }|null {
    if (text.codePointAt(position) !== MARKER_START) {
        return null
    }

    let end = position + 2
    let spelled = ''
    for (let codePoint = text.codePointAt(end); codePoint >= TAG_FIRST && codePoint <= TAG_LAST; codePoint = text.codePointAt(end)) {
        spelled += String.fromCharCode(codePoint - TAG_FIRST + 0x20)
        end += 2
    }

    if (text.codePointAt(end) === MARKER_END) {
        end += 2
    }

    return { kind: spelled.slice(0, 1), content: spelled.slice(1), end }
}

/**
 * What a suggestion inserts after the "@": the marker with the user's id and the name
 */
export function mentionText(user: MentionableUser): string {
    return marker('m', user.id) + user.name
}

/**
 * The mention markup of keyvue-app and one-app (keyvue-api stores it as it is)
 */
export function mentionMarkup(user: MentionableUser): string {
    const name = escapeHtml(user.name)

    return `<span class="mention" data-denotation-char="@" data-id="${user.id}" data-type="user" data-value="${name}">`
        + '﻿<span contenteditable="false"><span class="ql-mention-denotation-char">@</span>'
        + `<span class="keyvue-mention">${name}</span></span>﻿</span>`
}

interface Token {
    type: 'mention' | 'tag' | 'text'
    html: string
}

/**
 * The stored comment as mentions (the whole span with the spans in it), other tags and text. A "<" that starts no tag
 * (in a comment without markup, which keyvue-api stores as typed) is text.
 */
function tokenize(stored: string): Token[] {
    const tokens: Token[] = []
    let text = ''
    let position = 0

    const addText = () => {
        if (text !== '') {
            tokens.push({ type: 'text', html: text })
            text = ''
        }
    }

    while (position < stored.length) {
        if (stored[position] !== '<') {
            text += stored[position]
            position++

            continue
        }

        const end = stored.startsWith(MENTION_START, position) ? findEndOfSpan(stored, position) : null
        if (end !== null) {
            addText()
            tokens.push({ type: 'mention', html: stored.slice(position, end) })
            position = end

            continue
        }

        const tag = TAG.exec(stored.slice(position))
        if (tag) {
            addText()
            tokens.push({ type: 'tag', html: tag[0] })
            position += tag[0].length

            continue
        }

        text += '<'
        position++
    }

    addText()

    return tokens
}

/**
 * Where the span that starts at start ends (after its </span>), counting the spans in it; null without an end
 */
function findEndOfSpan(html: string, start: number): number|null {
    const spans = /<span\b|<\/span>/g
    spans.lastIndex = start
    let depth = 0

    for (let match = spans.exec(html); match !== null; match = spans.exec(html)) {
        depth += match[0] === '</span>' ? -1 : 1
        if (depth === 0) {
            return spans.lastIndex
        }
    }

    return null
}

export function commentToText(stored: string, users: MentionableUser[]): CommentText {
    let text = ''
    const keptMarkup: string[] = []
    const mentionNames: { [userId: string]: string } = {}

    for (const token of tokenize(stored)) {
        if (token.type === 'text') {
            // A no-break space (&nbsp;, as one-app's editor writes it) as a normal space
            text += decodeHtmlEntities(token.html).replace(/ /g, ' ')

            continue
        }

        if (token.type === 'mention') {
            const userId = /\bdata-id="([^"]*)"/.exec(token.html)
            const user = userId ? users.find(candidate => candidate.id === userId[1] && candidate.name) : undefined
            if (user) {
                text += `@${mentionText(user)}`
                mentionNames[user.id] = user.name

                continue
            }
        }

        if (token.type === 'tag' && LINE_BREAK.test(token.html)) {
            text += '\n'

            continue
        }

        // Shown and kept as stored, attributes and all, behind the marker with its number
        text += marker('k', String(keptMarkup.length)) + token.html
        keptMarkup.push(token.html)
    }

    return { text, stored, keptMarkup, mentionNames }
}

export function textToComment(text: string, users: MentionableUser[], from: CommentText|null): string {
    if (from !== null && text === from.text) {
        return from.stored
    }

    const usedMarkup = new Set<number>()
    let comment = ''
    let position = 0
    let inFlag = false

    while (position < text.length) {
        const codePoint = text.codePointAt(position)

        const found = readMarker(text, position)
        if (found !== null) {
            position = found.end

            const index = found.kind === 'k' && /^\d+$/.test(found.content) ? Number(found.content) : -1
            const markup = from !== null ? from.keptMarkup[index] : undefined
            if (markup !== undefined && !usedMarkup.has(index) && text.startsWith(markup, position)) {
                usedMarkup.add(index)
                comment += markup
                position += markup.length
            }

            // Otherwise the marker goes, and what follows it is text
            continue
        }

        if (codePoint >= TAG_FIRST && codePoint <= MARKER_END && !inFlag) {
            // What is left of a marker
            position += 2

            continue
        }

        if (text[position] === '@') {
            const mention = readMention(text, position, users, from)
            if (mention !== null) {
                comment += mentionMarkup(mention.user)
                position = mention.end

                continue
            }
        }

        const character = String.fromCodePoint(codePoint)
        inFlag = codePoint === FLAG || (inFlag && codePoint >= TAG_FIRST && codePoint <= MARKER_END)
        comment += character === '&' ? '&amp;' : character === '<' ? '&lt;' : character === '\n' ? '<br>' : character
        position += character.length
    }

    return comment
}

/**
 * The mention that starts with the "@" at position: by its marker (the user with that id, followed by their name as
 * it was shown), or else by a typed "@Name" that only one user has (the longest name that matches)
 */
function readMention(
    text: string,
    position: number,
    users: MentionableUser[],
    from: CommentText|null,
): { user: MentionableUser, end: number }|null {
    const found = readMarker(text, position + 1)
    if (found !== null) {
        if (found.kind !== 'm') {
            return null
        }

        const user = users.find(candidate => candidate.id === found.content && candidate.name)
        const name = user ? user.name : (from !== null ? from.mentionNames[found.content] : undefined)
        if (name && text.startsWith(name, found.end)) {
            return { user: { id: found.content, name }, end: found.end + name.length }
        }

        return null
    }

    return matchTypedMention(text, position, users)
}

/**
 * The typed mention whose "@" is at position: "@Name" as a word of its own (not inside an e-mail address such as
 * info@Bakker.nl, not the start of a longer word: "@Janneke" is not Jan), of the longest name that matches and that
 * only one user has
 */
function matchTypedMention<U extends MentionableUser>(
    text: string,
    position: number,
    users: U[],
): { user: U, end: number }|null {
    if (text[position] !== '@' || isWordCharacter(characterBefore(text, position))) {
        return null
    }

    const matching = users.filter(candidate => candidate.name
        && text.startsWith(`@${candidate.name}`, position)
        && !isWordCharacter(characterAt(text, position + 1 + candidate.name.length)))
    if (matching.length === 0) {
        return null
    }

    const longest = Math.max(...matching.map(candidate => candidate.name.length))
    const named = matching.filter(candidate => candidate.name.length === longest)
    if (named.length !== 1) {
        return null
    }

    return { user: named[0], end: position + 1 + longest }
}

/** The typed mentions in text (matchTypedMention), in order */
export function findTypedMentions<U extends MentionableUser>(
    text: string,
    users: U[],
): Array<{ start: number, end: number, user: U }> {
    const mentions: Array<{ start: number, end: number, user: U }> = []
    if (!text) {
        return mentions
    }

    let position = text.indexOf('@')
    while (position >= 0) {
        const mention = matchTypedMention(text, position, users)
        if (mention) {
            mentions.push({ start: position, end: mention.end, user: mention.user })
        }

        position = text.indexOf('@', mention ? mention.end : position + 1)
    }

    return mentions
}

/** text with each typed mention (findTypedMentions) replaced by render(user) */
export function renderTypedMentions<U extends MentionableUser>(
    text: string,
    users: U[],
    render: (user: U) => string,
): string {
    let result = ''
    let from = 0
    findTypedMentions(text, users).forEach(mention => {
        result += text.slice(from, mention.start) + render(mention.user)
        from = mention.end
    })

    return result + (text || '').slice(from)
}

/**
 * The typed mentions in a stored comment (HTML), in its text only: never inside a tag or its attributes (a link to
 * https://instagram.com/@Jan stays a link)
 */
export function findTypedMentionsInHtml<U extends MentionableUser>(html: string, users: U[]): U[] {
    return splitHtml(html)
        .filter((part, index) => index % 2 === 0)
        .flatMap(text => findTypedMentions(text, users).map(mention => mention.user))
}

/** A stored comment (HTML) with each typed mention in its text (findTypedMentionsInHtml) replaced by render(user) */
export function renderTypedMentionsInHtml<U extends MentionableUser>(
    html: string,
    users: U[],
    render: (user: U) => string,
): string {
    return splitHtml(html)
        .map((part, index) => index % 2 === 0 ? renderTypedMentions(part, users, render) : part)
        .join('')
}

/**
 * Text and tags, alternating: the even parts are text, the odd ones tags. A tag is what tokenize takes for one (TAG):
 * a "<" that starts none ("Bod < 300k") is text.
 */
function splitHtml(html: string): string[] {
    const source = html || ''
    const parts: string[] = []
    let text = ''
    let position = 0

    while (position < source.length) {
        TAG_AT.lastIndex = position
        const tag = source[position] === '<' ? TAG_AT.exec(source) : null
        if (tag) {
            parts.push(text, tag[0])
            text = ''
            position += tag[0].length

            continue
        }

        text += source[position]
        position++
    }

    parts.push(text)

    return parts
}

/** A letter, digit or underscore, in any script */
function isWordCharacter(character: string|null): boolean {
    return character !== null && /^[\p{L}\p{M}\p{N}_]$/u.test(character)
}

/** The character (a whole code point) that starts at position, or null at the end */
function characterAt(text: string, position: number): string|null {
    const codePoint = text.codePointAt(position)

    return codePoint === undefined ? null : String.fromCodePoint(codePoint)
}

/** The character (a whole code point) that ends just before position, or null at the start */
function characterBefore(text: string, position: number): string|null {
    if (position <= 0) {
        return null
    }

    const last = text.charCodeAt(position - 1)
    const isLowSurrogate = last >= 0xDC00 && last <= 0xDFFF
    const start = isLowSurrogate && position >= 2 ? position - 2 : position - 1

    return characterAt(text, start)
}
